import React, {useRef, useState, useCallback} from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { EyeTwoTone, EyeInvisibleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Captcha, {CaptchaHandle} from '../../../components/common/Captcha';
import {LoginFormProps} from "../../../model";
import {useAuth} from "../../../services";

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [captchaText, setCaptchaText] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth(); // 使用 AuthContext 提供的 login 方法

  const handleCaptchaChange = useCallback((captcha: string) => {
    setCaptchaText(captcha);
  }, []);

  const onFinish = async (values: any) => {
    if (values.captcha.toLowerCase() !== captchaText.toLowerCase()) {
      form.setFields([
        {
          name: 'captcha',
          errors: ['驗證碼不正確']
        }
      ]);
      return;
    }

    setLoading(true);
    try {
      // 使用 AuthContext 的 login 方法
      const success = await login(values.username, values.password);

      if (success) {
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        // 顯示歡迎訊息
        message.success(`歡迎回來，${values.username}!`);
        navigate('/');
      } else {
        message.error('登入失敗：無效的憑證');
      }
    } catch (error) {
      console.error('Login failed:', error);
      message.error('登入過程發生錯誤，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const captchaRef = useRef<CaptchaHandle>(null);

  const refreshCaptcha = () => {
    captchaRef.current?.refresh();
  };
  
  return (
    <Card
      title="登入"
      className="login-card"
      bordered={false}
    >
      <Form
        form={form}
        name="login_form"
        initialValues={{ remember: true }}
        onFinish={onFinish}
        layout="vertical"
      >
        <Form.Item
          name="username"
          rules={[{ required: true, message: '請輸入您的帳號' }]}
        >
          <Input 
            placeholder="帳號 Username"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[{ required: true, message: '請輸入您的密碼' }]}
        >
          <Input.Password
            placeholder="密碼 Password"
            iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="captcha"
          rules={[{ required: true, message: '請輸入驗證碼' }]}
        >
          <Input placeholder="請輸入驗證碼" size="large" />
        </Form.Item>
        
        <div className="captcha-container">
          <div className="captcha-wrapper">
            <Captcha
                ref={captchaRef}
                onChange={handleCaptchaChange}
            />
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={refreshCaptcha}
            type="text"
            className="refresh-button"
          />
        </div>

        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading} 
            size="large"
            className="login-button"
          >
            登入
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default LoginForm;
